import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  // ListItemText,
  Typography,
} from "@mui/material";

import React from "react";
import { ChevronRight, TaskAlt } from "@mui/icons-material";
import LandingRichText from "./LandingRichText";
import { checkIfContentEmpty } from "../../utils/tiptap";
import { LANDING_BRAND } from "./landingBrand";

type SectionHeader = {
  badge?: string;
  content?: string;
};

const hasHeaderContent = (header?: SectionHeader) => {
  if (header?.badge?.trim()) {
    return true;
  }
  return Boolean(header?.content?.trim() && !checkIfContentEmpty(header.content));
};

const EligibilityBenefits = ({
  header,
  eligibilityItems,
  benefitItems,
}: {
  header?: SectionHeader;
  eligibilityItems?: string[];
  benefitItems?: string[];
}) => {
  const showHeader = hasHeaderContent(header);
  const eligibility = eligibilityItems?.filter((item) => item?.trim()) ?? [];
  const benefits = benefitItems?.filter((item) => item?.trim()) ?? [];

  if (!showHeader && eligibility.length === 0 && benefits.length === 0) {
    return null;
  }

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 10 },
        backgroundColor: "#f9fafb",
      }}
    >
      <Container>
        {showHeader && (
          <Box textAlign="center" sx={{ mb: "64px" }}>
            {header?.badge?.trim() && (
              <Typography
                variant="body1"
                fontWeight="regular"
                sx={{
                  display: "inline-block",
                  mb: 2,
                  p: "4px 16px",
                  borderRadius: 999,
                  bgcolor: LANDING_BRAND.primaryLight,
                  color: LANDING_BRAND.primaryDark,
                  lineHeight: "1.25rem",
                }}
              >
                {header.badge}
              </Typography>
            )}
            <LandingRichText
              html={header?.content}
              sx={{
                fontSize: "1.25rem",
                color: "#4B5563",
                lineHeight: "1.75rem",
                maxWidth: "48rem",
                mx: "auto",
                textAlign: "center",
                "& p": { margin: "0 0 0.75rem", color: "inherit" },
                "& p:last-child": { marginBottom: 0 },
                "& h1, & h2, & h3": {
                  fontWeight: "bold",
                  color: "text.primary",
                  mb: 2,
                },
                "& h1": {
                  fontSize: { xs: "1.875rem", md: "2.25rem" },
                  lineHeight: "2.5rem",
                },
                "& h2": {
                  fontSize: { xs: "1.5rem", md: "1.875rem" },
                  lineHeight: "2.25rem",
                },
                "& h3": {
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                  lineHeight: "2rem",
                },
                "& ul, & ol": {
                  margin: "0.5rem auto",
                  paddingLeft: "1.5rem",
                  textAlign: "left",
                  display: "inline-block",
                },
                "& a": { color: LANDING_BRAND.primaryDark },
              }}
            />
          </Box>
        )}
        {(eligibility.length > 0 || benefits.length > 0) && (
          <Grid container spacing={4} alignItems="stretch">
            {eligibility.length > 0 && (
              <Grid item xs={12} md={benefits.length > 0 ? 6 : 12}>
                <Card
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: "1rem",
                    boxShadow:
                      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
                    height: "100%",
                  }}
                >
                  <CardContent sx={{ p: "32px !important", flexGrow: 1 }}>
                    <List dense>
                      {eligibility.map((text: string, index: number) => (
                        <ListItem
                          key={index}
                          disableGutters
                          sx={{
                            alignItems: "flex-start",
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <Box
                              sx={{
                                width: "1.5rem",
                                height: "1.5rem",
                                borderRadius: "999px",
                                bgcolor: LANDING_BRAND.primaryLight,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <ChevronRight
                                sx={{
                                  color: LANDING_BRAND.primaryDark,
                                  width: "0.75rem",
                                  height: "0.75rem",
                                }}
                              />
                            </Box>
                          </ListItemIcon>
                          {/* <ListItemText
                          primary={text}
                          sx={{
                            "& > span": {
                              fontSize: "16px",
                              lineHeight: "24px",
                              color: "#374151",
                            },
                          }}
                        /> */}
                          <Typography
                            variant="subtitle1"
                            sx={{
                              lineHeight: "24px",
                              color: "#374151",
                            }}
                          >
                            {text}
                          </Typography>
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            )}
            {benefits.length > 0 && (
              <Grid item xs={12} md={eligibility.length > 0 ? 6 : 12} alignItems="stretch">
                <Card
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: "1rem",
                    boxShadow:
                      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
                    height: "100%",
                  }}
                >
                  <CardContent sx={{ p: "32px !important", flexGrow: 1 }}>
                    <List dense>
                      {benefits.map((text: string, index: number) => (
                        <ListItem
                          key={index}
                          disableGutters
                          sx={{
                            alignItems: "flex-start",
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <Box
                              sx={{
                                width: "1.5rem",
                                height: "1.5rem",
                                borderRadius: "999px",
                                bgcolor: LANDING_BRAND.primaryLight,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <TaskAlt
                                sx={{
                                  color: LANDING_BRAND.primaryDark,
                                  width: "0.75rem",
                                  height: "0.75rem",
                                }}
                              />
                            </Box>
                          </ListItemIcon>
                          {/* <ListItemText
                          primary={text}
                          sx={{
                            "& > span": {
                              fontSize: "16px",
                              lineHeight: "24px",
                              color: "#374151",
                            },
                          }}
                        /> */}
                          <Typography
                            variant="subtitle1"
                            sx={{
                              lineHeight: "24px",
                              color: "#374151",
                            }}
                          >
                            {text}
                          </Typography>
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default EligibilityBenefits;
